import { useEffect, useState, useRef, type KeyboardEvent } from "react"
import { Icon } from "@/components/Icon"
import { useComposerStore } from "@/features/chat/composer.store"
import { useFileDrop } from "@/hooks/useFileDrop"

interface ComposerProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  /** Opt-in file attach (mid-review evidence). Only surfaces the control
   *  when provided — the plain chat composer stays unchanged. */
  onAttach?: (file: File) => Promise<void> | void
  attachBusy?: boolean
}

export function Composer({ onSend, disabled, placeholder, onAttach, attachBusy }: ComposerProps) {
  const [value, setValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isDragging, dropHandlers } = useFileDrop(
    (files) => {
      if (onAttach && files[0]) void onAttach(files[0])
    },
    disabled || attachBusy || !onAttach,
  )
  const { prefill, prefillNonce, clearPrefill } = useComposerStore()

  // Seed the composer when another part of the app requests a revision
  // (e.g. "Send back" on a draft section). Focus so the officer can edit/send.
  useEffect(() => {
    if (!prefill) return
    setValue(prefill)
    clearPrefill()
    const el = textareaRef.current
    if (el) {
      el.focus()
      requestAnimationFrame(() => {
        el.style.height = "auto"
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`
        el.setSelectionRange(el.value.length, el.value.length)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillNonce])

  const handleSend = () => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend(trimmed)
    setValue("")
    if (textareaRef.current) textareaRef.current.style.height = "auto"
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }

  return (
    <div
      className={"chat__compose" + (onAttach && isDragging ? " chat__compose--drop" : "")}
      {...(onAttach ? dropHandlers : {})}
    >
      {onAttach && isDragging && (
        <div className="na-dropzone__overlay na-dropzone__overlay--chat">
          Drop a file to add it as new evidence
        </div>
      )}
      <div className={"composer" + (disabled ? " composer--locked" : "")}>
        {onAttach && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.txt,.md,.csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void onAttach(file)
                e.target.value = ""
              }}
            />
            <button
              className="composer__send"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || attachBusy}
              aria-label="Attach evidence file"
              title="Add new evidence (PDF, image, docx, text)"
            >
              <Icon name={attachBusy ? "loader" : "paperclip"} size={16} />
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={placeholder ?? "Ask anything about this case…"}
          disabled={disabled}
          rows={1}
        />
        <button
          className="composer__send"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          aria-label="Send message"
        >
          <Icon name="send" size={16} />
        </button>
      </div>
    </div>
  )
}
